import { ClaimRecord, ClaimRecordStatus, EligibleProductsResult } from '@typesApp/ClaimRecord'
import { CartBundle, EligibleProductStatus, ProductBundle } from '@typesApp/Product'
import { getEligibleProductStatus, shouldCheckEligibleProducts } from '@utils/claimUtil'
import { isProductInsuranceEligible } from '@utils/isProductInsuranceEligible'

type ControllerService = {
  fetchClaimRecord: () => Promise<ClaimRecord | null>
  fetchCartBundle: () => Promise<CartBundle>
  fetchEligibleProducts: (products: ProductBundle[]) => Promise<EligibleProductsResult>

  onComplete: (status: EligibleProductStatus) => Promise<void>
  onError: (error?: unknown) => Promise<void>
}

type EligibleProductControllerReturn = {
  checkEligibility: () => Promise<void>
}

export const eligibleProductController = (
  service: ControllerService,
  claimRecordId: string
): EligibleProductControllerReturn => {
  const checkEligibility = async (): Promise<void> => {
    try {
      const status = claimRecordId ? await checkWithClaimRecord() : await checkWithCartBundle()
      await service.onComplete(status)
    } catch (error) {
      await service.onError(error)
    }
  }

  const checkWithClaimRecord = async (): Promise<EligibleProductStatus> => {
    // re-fetch to ensure we have latest eligible products in claim record
    const claimRecord = await service.fetchClaimRecord()

    // skip if restricted
    if (claimRecord?.status === ClaimRecordStatus.RESTRICTED) {
      return EligibleProductStatus.NA
    }

    // NOTE: claim record may not immediately reflect the status on re-fetch after cart change.
    // In addition, some brands (e.g. VDE) have insurer specific eligibility restrictions so
    // the products in the claim record may NOT be eligible (due to the insurer) but the cart
    // products may be eligible.

    // Hence, we need to use the cart items and check-insurance-eligibility API if the status
    // from the claim record is NOT_ELIGIBLE
    const eligibleProducts = claimRecord?.eligibleProducts ?? []
    let status = getEligibleProductStatus(eligibleProducts)

    // In CA flow the eligibleProducts should not be empty but it may be if commerce
    // fails to call B/E cart update correctly. In this case we need to fetch cart to be sure.
    const shouldFetchCart = status === EligibleProductStatus.NOT_ELIGIBLE || eligibleProducts.length === 0
    if (shouldFetchCart && shouldCheckEligibleProducts(claimRecord?.status)) {
      status = await checkWithCartBundle()
    }

    return status
  }

  const checkWithCartBundle = async (): Promise<EligibleProductStatus> => {
    const cartBundle = await service.fetchCartBundle()
    const products = cartBundle?.products ?? []

    let eligibleProducts = products

    // perform eligibility check only if we have cart products
    if (products.length > 0) {
      const result = await service.fetchEligibleProducts(products)
      const cartProducts = result?.eligibleProducts || []
      eligibleProducts = cartProducts.filter(isProductInsuranceEligible)
    }

    return eligibleProducts.length > 0 ? EligibleProductStatus.ELIGIBLE : EligibleProductStatus.NOT_ELIGIBLE
  }

  return {
    checkEligibility,
  }
}
